/**
 * API client for the Azure Functions backend.
 * In production, set REACT_APP_API_BASE to your Function App URL.
 * In development, it defaults to localhost:7071.
 */
const API_BASE = process.env.REACT_APP_API_BASE || 'https://rmg-reports-func-gmgshjbwguhybzaa.westus2-01.azurewebsites.net/api';

/**
 * Auth helpers — token stored in sessionStorage.
 */
export function getToken() {
  return sessionStorage.getItem('rmg_token');
}

export function setToken(token) {
  sessionStorage.setItem('rmg_token', token);
}

export function clearToken() {
  sessionStorage.removeItem('rmg_token');
  sessionStorage.removeItem('rmg_auth');
}

export function getAuthInfo() {
  const raw = sessionStorage.getItem('rmg_auth');
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setAuthInfo(info) {
  sessionStorage.setItem('rmg_auth', JSON.stringify(info));
}

/**
 * Build headers with auth token.
 */
function authHeaders() {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
}

/**
 * Login — returns auth info on success, throws on failure.
 */
export async function login(customer, password) {
  const resp = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ customer, password }),
  });

  const data = await resp.json();

  if (!resp.ok) {
    throw new Error(data.error || 'Login failed');
  }

  setToken(data.token);
  setAuthInfo(data);
  return data;
}

/**
 * Authenticated fetch wrapper — redirects to login on 401.
 */
async function authFetch(url) {
  const resp = await fetch(url, { headers: authHeaders() });

  if (resp.status === 401) {
    clearToken();
    window.location.href = '/';
    throw new Error('Session expired — please log in again');
  }

  if (resp.status === 403) {
    throw new Error('You do not have access to this report');
  }

  if (!resp.ok) {
    throw new Error(`Request failed: ${resp.statusText}`);
  }

  return resp.json();
}

export async function fetchLatestReport(customer) {
  return authFetch(`${API_BASE}/reports/latest/${customer}`);
}

export async function fetchHistoryDates(customer) {
  return authFetch(`${API_BASE}/reports/history/${customer}`);
}

export async function fetchHistoricalReport(customer, date) {
  return authFetch(`${API_BASE}/reports/history/${customer}/${date}`);
}

export async function fetchMetadata() {
  return authFetch(`${API_BASE}/reports/metadata`);
}

export async function fetchInventory(customer) {
  return authFetch(`${API_BASE}/reports/inventory/${customer}`);
}

export async function fetchMetrics(customer, startMonth, endMonth) {
  const params = new URLSearchParams();
  if (startMonth) params.set('start', startMonth);
  if (endMonth) params.set('end', endMonth);
  return authFetch(`${API_BASE}/reports/metrics/${customer}?${params.toString()}`);
}

export function getDownloadUrl(customer, date) {
  const token = getToken();
  // Pass token as query param for direct download links
  return `${API_BASE}/reports/download/${customer}/${date}?token=${encodeURIComponent(token || '')}`;
}

export function getMonthlyReviewUrl(customer) {
  const token = getToken();
  return `${API_BASE}/reports/monthly-review/${customer}?token=${encodeURIComponent(token || '')}`;
}
